import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { Divider, Flex, HStack, VStack } from '@chakra-ui/react';
import { TooltipProps } from 'recharts';
import {
  NameType,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';

import { formatAmount } from 'common/formatAmount';

import GraphTooltipLabel from './components/GraphTooltipLabel';

interface GraphLabel {
  title: string | null;
  value: number | null;
}

interface GraphTooltipProps extends TooltipProps<ValueType, NameType> {
  labels: GraphLabel[];
  vertical?: boolean;
  marginLeft?: string;
}

const GraphTooltip: FC<GraphTooltipProps> = ({
  labels,
  marginLeft,
  vertical = false,
  ...props
}) => {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  const [isLastPoints, setIsLastPoints] = useState(false);
  const { coordinate, viewBox } = props;

  // Value to compensate the offset's size depending of tooltip margins
  const leftMarginCompensation = 3;
  const rightMarginCompensation = 13;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (elementRef.current?.offsetWidth) {
      const tooltipComponentWidth = elementRef.current.offsetWidth;
      setWidth(tooltipComponentWidth);

      if (coordinate?.x && viewBox?.width) {
        // True if the second half of the tooltip exceed the width of the graph
        // in this case we need to use a different margin direction
        setIsLastPoints(
          coordinate.x + tooltipComponentWidth / 2 > viewBox.width,
        );
      }
    }
  });

  const Container = vertical ? VStack : HStack;

  return (
    <Flex
      alignItems="center"
      flexDirection="column"
      justifyContent="center"
      {...(isLastPoints
        ? {
            marginRight: -(width / 2) - rightMarginCompensation,
          }
        : {
            marginLeft: -(width / 2) - leftMarginCompensation,
          })}
      ref={elementRef}
    >
      <Container
        backgroundColor="grey.800"
        borderRadius="radius-10"
        marginLeft={marginLeft}
        minHeight="50px"
        padding="space-8"
      >
        {labels.map(({ title, value }, index) => {
          if (value === null || value === undefined || !title) {
            return null;
          }

          const tooltipLabel = (
            <GraphTooltipLabel
              key={index}
              text={formatAmount({
                forcePlusSign: false,
                isMinorUnitValue: true,
                value,
              })}
              title={title}
            />
          );

          // @ts-expect-error Object is possibly 'undefined'.
          if (labels[index + 1] && labels[index + 1].value) {
            return (
              <Fragment key={index}>
                {tooltipLabel}
                <Divider
                  borderColor="grey.700"
                  height={vertical ? undefined : '25px'}
                  orientation={vertical ? 'horizontal' : 'vertical'}
                />
              </Fragment>
            );
          }

          return tooltipLabel;
        })}
      </Container>
    </Flex>
  );
};

export default GraphTooltip;
